import React, { useState, useEffect, useCallback } from "react";
import { Modal, Collapse, Popconfirm, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import EventForm from './EventForm';
import api from '../../utils/api'; // Import the API utility
import log from '../../utils/logger'; // Import the logger

const { Panel } = Collapse;

const Events = () => {
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState({});
  const [formState, setFormState] = useState({
    eventName: "",
    eventDate: "",
    category: "fixed",
    recurrence: "one-time",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [currentEventId, setCurrentEventId] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [initialEventName, setInitialEventName] = useState("");

  const { eventName, eventDate, category, recurrence } = formState;

  useEffect(() => {
    fetchActiveEvents();
    fetchCategories();
  }, []);

  const fetchActiveEvents = useCallback(async () => {
    try {
      const response = await api.get("/event/get-active-event");
      if (response.data.code === 200) {
        setEvents(response.data.data);
      } else {
        log.error(`Failed to fetch active events: ${response.data.message}`);
        message.error('Failed to fetch active events');
      }
    } catch (error) {
      log.error("Error fetching active events:", error);
      message.error('Error fetching active events');
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get("/category/get-active-categories");
      if (response.data.code === 200) {
        const categoriesMap = {};
        response.data.data.forEach(category => {
          categoriesMap[category.name] = category._id;
        });
        setCategories(categoriesMap);
      } else {
        log.error(`Failed to fetch active categories: ${response.data.message}`);
        message.error('Failed to fetch active categories');
      }
    } catch (error) {
      log.error("Error fetching active categories:", error);
      message.error('Error fetching active categories');
    }
  }, []);

  const handleCreateOrUpdateEvent = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      let eventResponse;

      const payload = {
        name: eventName,
        category,
        recurrence,
        ...(category === "fixed" ? { date: eventDate } : {}),
      };

      if (isEditing) {
        eventResponse = await api.put(`/event/update-event/${currentEventId}`, payload);
      } else {
        eventResponse = await api.post("/event/create-event", payload);
      }

      if (eventResponse.data.code === 200) {
        const categoryPayload = { name: eventName };
        let categoryResponse;

        if (isEditing) {
          const categoryId = categories[initialEventName]; // Use initialEventName to find the existing category
          if (categoryId) {
            categoryResponse = await api.put(`/category/edit-category/${categoryId}`, categoryPayload);
          }
        } else {
          categoryResponse = await api.post("/category/create-category", categoryPayload);
        }

        if (categoryResponse && categoryResponse.data.code !== 200) {
          log.error(`Failed to create/update category: ${categoryResponse.data.message}`);
          message.error('Failed to create/update category');
        } else {
          fetchActiveEvents();
          fetchCategories(); // Refresh categories
          resetForm();
          setIsModalVisible(false);
          message.success(isEditing ? 'Event and Category updated successfully' : 'Event and Category created successfully');
        }
      } else if (eventResponse.data.code === 400 && eventResponse.data.message === "Event already exists!") {
        message.warning('Event already exists!');
      } else {
        log.error(`Failed to create/update event: ${eventResponse.data.message}`);
        message.error('Failed to create/update event');
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.message === "Event already exists!") {
        message.warning('Event already exists!');
      } else {
        log.error("Error creating/updating event:", error);
        message.error('An error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditEvent = useCallback((event) => {
    setFormState({
      eventName: event.name,
      eventDate: event.category === "fixed" ? new Date(event.date).toISOString().slice(0, 10) : "",
      category: event.category,
      recurrence: event.recurrence,
    });
    setInitialEventName(event.name); // Set the initial event name to track changes
    setIsEditing(true);
    setCurrentEventId(event._id);
    setIsModalVisible(true);
  }, []);

  const handleDeleteEvent = async (id) => {
    try {
      const eventResponse = await api.delete(`/event/delete-event/${id}`);
      if (eventResponse.data.code === 200) {
        const event = events.find((ev) => ev._id === id);
        if (event) {
          const categoryId = categories[event.name];
          if (categoryId) {
            const categoryResponse = await api.delete(`/category/delete-category/${categoryId}`);
            if (categoryResponse.data.code === 200) {
              fetchActiveEvents();
              fetchCategories(); // Refresh categories
              message.success('Event and Category deleted successfully');
            } else {
              log.error(`Failed to delete category: ${categoryResponse.data.message}`);
              message.error('Failed to delete category');
            }
          } else {
            fetchActiveEvents();
            message.success('Event deleted successfully, but no associated category found');
          }
        }
      } else {
        log.error(`Failed to delete event: ${eventResponse.data.message}`);
        message.error('Failed to delete event');
      }
    } catch (error) {
      log.error("Error deleting event:", error);
      message.error('An error occurred');
    }
  };

  const resetForm = () => {
    setFormState({
      eventName: "",
      eventDate: "",
      category: "fixed",
      recurrence: "one-time",
    });
    setInitialEventName("");
    setIsEditing(false);
    setCurrentEventId(null);
  };

  const showModal = () => {
    resetForm();
    setIsModalVisible(true);
  };

  const hideModal = () => {
    resetForm();
    setIsModalVisible(false);
  };

  const renderEvent = (event) => (
    <Panel header={event.name} key={event._id}>
      <p>Category: {event.category === "fixed" ? "Fixed (Admin Sets Date)" : "Not Fixed (User Sets Date)"}</p>
      <p>Recurrence: {event.recurrence}</p>
      {event.category === "fixed" && <p>Date: {new Date(event.date).toLocaleDateString()}</p>}
      <div className="flex space-x-4 mt-4">
        <button
          onClick={() => handleEditEvent(event)}
          className="bg-blue-600 text-white px-4 py-2 squared hover:bg-blue-800"
        >
          <EditOutlined /> Edit
        </button>
        <Popconfirm
          title="Are you sure delete this event?"
          onConfirm={() => handleDeleteEvent(event._id)}
          onCancel={() => message.info('Deletion cancelled')}
          okText="Yes"
          cancelText="No"
        >
          <button className="bg-primary text-white px-4 py-2 squared hover:bg-primary-dark">
            <DeleteOutlined /> Delete
          </button>
        </Popconfirm>
      </div>
    </Panel>
  );

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-end mb-4">
        <button
          onClick={showModal}
          className="bg-primary text-white px-4 py-2 squared hover:bg-primary-dark"
        >
          <PlusOutlined /> Add Event
        </button>
      </div>
      <Modal
        title={isEditing ? "Edit Event" : "Add Event"}
        open={isModalVisible}
        onCancel={hideModal}
        footer={null}
        className="squared"
      >
        <EventForm
          eventName={eventName}
          setEventName={(value) => setFormState((prev) => ({ ...prev, eventName: value }))}
          category={category}
          setCategory={(value) => setFormState((prev) => ({ ...prev, category: value }))}
          recurrence={recurrence}
          setRecurrence={(value) => setFormState((prev) => ({ ...prev, recurrence: value }))}
          eventDate={eventDate}
          setEventDate={(value) => setFormState((prev) => ({ ...prev, eventDate: value }))}
          isEditing={isEditing}
          handleSubmit={handleCreateOrUpdateEvent}
          hideModal={hideModal}
          isLoading={isLoading}
        />
      </Modal>
      <Collapse accordion>
        {events.map(renderEvent)}
      </Collapse>
    </div>
  );
};

export default Events;
